// src/edu-world/systems/standalone-launcher.ts
// Standalone_Launcher: opens a standalone EduAdventure game in an iframe overlay.
// Communicates via postMessage to detect game completion.
// Used for the 3 EduWorld standalone missions (Horta dos Números, Letras do Parque, Bichos e Padrões).

import type { MinigameInput, MinigameOutput } from '../types';
import type { EventBus } from '../core/event-bus';

export class StandaloneLauncher {
  private eventBus: EventBus;
  private overlay: HTMLDivElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private isActive: boolean = false;
  private currentMissionId: string = '';
  private startTime: number = 0;
  private messageHandler: ((event: MessageEvent) => void) | null = null;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  /** Check if a minigame config is a standalone type */
  static isStandalone(input: MinigameInput): boolean {
    return input.mechanic_type === 'standalone';
  }

  /** Launch standalone game in iframe overlay */
  launch(input: MinigameInput): void {
    if (this.isActive) return;
    this.isActive = true;
    this.currentMissionId = input.mission_id;
    this.startTime = Date.now();

    const standalonePath = (input.config.data as Record<string, unknown>).standalone_path as string;
    if (!standalonePath) {
      console.error('[StandaloneLauncher] No standalone_path in config');
      this.isActive = false;
      return;
    }

    // Create overlay container
    this.overlay = document.createElement('div');
    this.overlay.id = 'standalone-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    `;

    // Create close button (back to world)
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕ Voltar ao Parque';
    closeBtn.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 10000;
      font-size: 16px;
      padding: 10px 18px;
      border-radius: 10px;
      border: 2px solid rgba(255,255,255,0.3);
      background: rgba(20, 33, 61, 0.9);
      color: #fff;
      cursor: pointer;
      font-family: system-ui, sans-serif;
    `;
    closeBtn.addEventListener('click', () => this.handleClose(false));
    this.overlay.appendChild(closeBtn);

    // Create iframe
    this.iframe = document.createElement('iframe');
    this.iframe.src = standalonePath;
    this.iframe.style.cssText = `
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 0;
    `;
    this.iframe.allow = 'autoplay';
    this.overlay.appendChild(this.iframe);

    document.body.appendChild(this.overlay);

    // Listen for postMessage from the standalone game (completion signal)
    this.messageHandler = (event: MessageEvent) => {
      if (event.data && event.data.type === 'eduadventure:complete') {
        this.handleClose(true);
      }
      if (event.data && event.data.type === 'eduadventure:exit') {
        this.handleClose(false);
      }
    };
    window.addEventListener('message', this.messageHandler);
  }

  /** Handle close — either via completion or manual exit */
  private handleClose(success: boolean): void {
    if (!this.isActive) return;
    this.isActive = false;

    // Fade out the overlay smoothly before removing
    if (this.overlay) {
      this.overlay.style.transition = 'opacity 0.6s ease-out';
      this.overlay.style.opacity = '0';
    }

    // Remove message listener immediately
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }

    // Calculate duration
    const durationSeconds = Math.round((Date.now() - this.startTime) / 1000);

    // Delay removal to allow fade animation
    setTimeout(() => {
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
      this.overlay = null;
      this.iframe = null;

      // Emit completion event (same format as the existing minigame system)
      const output: MinigameOutput = {
        mission_id: this.currentMissionId,
        success,
        score: success ? 100 : 0,
        attempts: 1,
        duration_seconds: durationSeconds,
      };

      this.eventBus.emit('minigame:complete', output);
    }, 700);
  }

  getIsActive(): boolean {
    return this.isActive;
  }
}
